import { useCallback, useRef, useState } from 'react';
import type { DocumentV3 } from '@docgen/schema';

const HISTORY_LIMIT = 80;

export interface DocHistory {
  doc: DocumentV3 | null;
  /** apply a new state; same coalesceKey twice in a row amends instead of pushing */
  set: (next: DocumentV3, coalesceKey?: string) => void;
  reset: (next: DocumentV3 | null) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

/** Bounded snapshot undo/redo over the whole document (structural sharing
 *  keeps snapshots cheap; text edits coalesce by key). */
export function useDocHistory(): DocHistory {
  const [doc, setDoc] = useState<DocumentV3 | null>(null);
  const past = useRef<DocumentV3[]>([]);
  const future = useRef<DocumentV3[]>([]);
  const lastKey = useRef<string | undefined>(undefined);
  const [, bump] = useState(0);

  const set = useCallback((next: DocumentV3, coalesceKey?: string) => {
    setDoc((prev) => {
      if (prev) {
        const coalesce = coalesceKey !== undefined && coalesceKey === lastKey.current;
        if (!coalesce) {
          past.current = [...past.current.slice(-HISTORY_LIMIT + 1), prev];
        }
      }
      lastKey.current = coalesceKey;
      future.current = [];
      return next;
    });
    bump((n) => n + 1);
  }, []);

  const reset = useCallback((next: DocumentV3 | null) => {
    past.current = [];
    future.current = [];
    lastKey.current = undefined;
    setDoc(next);
    bump((n) => n + 1);
  }, []);

  const undo = useCallback(() => {
    setDoc((prev) => {
      const last = past.current[past.current.length - 1];
      if (!prev || !last) return prev;
      past.current = past.current.slice(0, -1);
      future.current = [prev, ...future.current];
      lastKey.current = undefined;
      return last;
    });
    bump((n) => n + 1);
  }, []);

  const redo = useCallback(() => {
    setDoc((prev) => {
      const next = future.current[0];
      if (!prev || !next) return prev;
      future.current = future.current.slice(1);
      past.current = [...past.current, prev];
      lastKey.current = undefined;
      return next;
    });
    bump((n) => n + 1);
  }, []);

  return {
    doc,
    set,
    reset,
    undo,
    redo,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
  };
}
